import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { OverlayToggle } from '~/components/dvf/DeformationDisplayControls';
import DeformationFieldPanel from '~/components/dvf/DeformationFieldPanel';
import { useDeformationDisplayScope } from '~/hooks/viewer/useDeformationDisplayScope';
import { useDeformationUncertainty } from '~/hooks/viewer/useDeformationUncertainty';
import { useSelectedDeformationField } from '~/hooks/viewer/useSelectedDeformationField';
import { isroDeploymentEnabled } from '~/hooks/workflows/isro/isroSamples';
import { useIsroComparison } from '~/hooks/workflows/isro/useIsroComparison';
import { deformationUncertaintyEnabledState } from '~/state/atoms/deformationDisplayAtoms';
import { infernoGradientCss } from '~/utils/colormap';
import { formatDistanceCm } from '~/utils/measurementFormat';

function UncertaintyLegend({
    maxMm,
    sampleCount,
}: {
    maxMm: number;
    sampleCount: number;
}) {
    return (
        <Box data-testid="isro-uncertainty-legend">
            <Box
                height={8}
                borderRadius={0.5}
                sx={{ background: infernoGradientCss('to right') }}
            />
            <Box display="flex" justifyContent="space-between" mt={0.25}>
                <Typography fontSize={10} color="text.secondary">
                    {formatDistanceCm(0)}
                </Typography>
                <Typography fontSize={10} color="text.secondary">
                    {formatDistanceCm(maxMm)}
                </Typography>
            </Box>
            <Typography fontSize={10} color="text.secondary">
                <FormattedMessage
                    id="isro.uncertaintyNote"
                    values={{ n: sampleCount }}
                />
            </Typography>
        </Box>
    );
}

export default function IsroResultPanel() {
    const intl = useIntl();
    const {
        result,
        selectedNumber,
        referenceOnScreen,
        hasDeformation,
        otherComparisonOpen,
        select,
        close,
    } = useIsroComparison();
    const field = useSelectedDeformationField();
    const uncertainty = useDeformationUncertainty();
    const [uncertaintyOn, setUncertaintyOn] = useRecoilState(
        deformationUncertaintyEnabledState
    );
    const comparing = selectedNumber !== null;
    useDeformationDisplayScope(comparing);

    // Leaving the results ends their comparison: the sample pair and its uncertainty belong to this panel.
    const closeRef = useRef(close);
    closeRef.current = close;
    useEffect(() => () => closeRef.current(), []);

    // Opens on the first sample once per result, while the reference is shown.
    const presentedFor = useRef<string | null>(null);
    useEffect(() => {
        if (
            !result ||
            !referenceOnScreen ||
            otherComparisonOpen ||
            presentedFor.current === result.jobId
        ) {
            return;
        }
        presentedFor.current = result.jobId;
        select(result.samples[0]);
    }, [result, referenceOnScreen, otherComparisonOpen, select]);

    if (!result || !isroDeploymentEnabled()) {
        return null;
    }

    return (
        <Box
            px={1.5}
            py={1}
            display="flex"
            flexDirection="column"
            gap={1}
            data-testid="isro-result-panel"
        >
            <Box>
                <Typography fontSize={12} color="primary" fontWeight={500}>
                    <FormattedMessage
                        id="isro.title"
                        values={{ model: result.modelName }}
                    />
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                    <FormattedMessage
                        id="isro.sampleCount"
                        values={{ n: result.samples.length }}
                    />
                </Typography>
            </Box>

            {!referenceOnScreen && (
                <Typography
                    fontSize={11}
                    color="warning.main"
                    data-testid="isro-reference-required"
                >
                    <FormattedMessage id="isro.showReference" />
                </Typography>
            )}

            <ToggleButtonGroup
                exclusive
                size="small"
                value={selectedNumber}
                disabled={!referenceOnScreen}
                onChange={(_, number: number | null) => {
                    const sample = result.samples.find(
                        (candidate) => candidate.number === number
                    );
                    if (sample) {
                        select(sample);
                    }
                }}
                sx={{ flexWrap: 'wrap' }}
            >
                {result.samples.map((sample) => (
                    <ToggleButton
                        key={sample.seriesUid}
                        value={sample.number}
                        aria-label={intl.formatMessage(
                            { id: 'isro.sample' },
                            { n: sample.number }
                        )}
                        data-testid={`isro-sample-${sample.number}`}
                        sx={{ minWidth: 32, py: 0.25, fontSize: 11 }}
                    >
                        {sample.number}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {!hasDeformation && (
                <Typography
                    fontSize={11}
                    color="warning.main"
                    data-testid="isro-no-deformation"
                >
                    <FormattedMessage id="isro.noDeformation" />
                </Typography>
            )}

            {comparing && field && (
                <DeformationFieldPanel field={field}>
                    {uncertainty && (
                        <>
                            <OverlayToggle
                                labelId="isro.uncertainty"
                                checked={uncertaintyOn}
                                onChange={setUncertaintyOn}
                                testId="isro-uncertainty-toggle"
                            />
                            {uncertaintyOn && (
                                <UncertaintyLegend
                                    maxMm={uncertainty.maxMm}
                                    sampleCount={uncertainty.sampleCount}
                                />
                            )}
                        </>
                    )}
                </DeformationFieldPanel>
            )}

            {comparing && (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={close}
                    data-testid="isro-close"
                    sx={{ textTransform: 'none', fontSize: 12 }}
                >
                    <FormattedMessage id="isro.close" />
                </Button>
            )}
        </Box>
    );
}
